/**
 * Fetches a static evidence bundle (`/demo/bundle/...`, generated for real by
 * `scripts/generate-evidence-bundle.ts`) and validates every file against
 * `@exploitlens/ui-contracts`'s schemas before building the view models the
 * routes render. Every failure -- a missing file, a network error, a file
 * that doesn't parse as JSON, a file that fails schema validation -- is
 * returned as an explicit `Result` error; nothing here silently substitutes
 * placeholder data.
 */
import {
  AlignmentFileSchema,
  ArtifactIndexViewSchema,
  buildCausalSeam,
  buildCaseOverview,
  buildReplayRail,
  DivergenceFileSchema,
  EffectsFileSchema,
  err,
  IntelligenceAttestationViewSchema,
  ManifestViewSchema,
  ok,
  ReceiptViewSchema,
  TraceSchema,
  VariantLedgerViewSchema,
  VerificationResultViewSchema,
  type AlignmentFile,
  type DivergenceFile,
  type EvidenceBundleView,
  type Result,
} from "@exploitlens/ui-contracts";
import type { z } from "zod";

export const DEFAULT_BUNDLE_BASE = "/demo";

async function fetchJson<T>(
  url: string,
  schema: z.ZodType<T>,
  { optional = false }: { optional?: boolean } = {},
): Promise<Result<T | undefined>> {
  let response: Response;
  try {
    response = await fetch(url);
  } catch (cause) {
    return err(
      "ARTIFACT_MISSING",
      `network error fetching "${url}": ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  if (!response.ok) {
    if (optional && response.status === 404) return ok(undefined);
    return err("ARTIFACT_MISSING", `failed to fetch "${url}": HTTP ${response.status}`);
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (cause) {
    return err(
      "SCHEMA_VALIDATION",
      `"${url}" is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    return err("SCHEMA_VALIDATION", `"${url}" failed schema validation`, {
      issues: parsed.error.issues,
    });
  }
  return ok(parsed.data);
}

export interface LoadBundleOptions {
  /** Base path the bundle and its verification results are served under. Defaults to `/demo`. */
  baseUrl?: string;
}

/**
 * Loads and validates one case's full evidence bundle. `alignment.json` and
 * `divergence.json` are treated as optional, matching `@exploitlens/
 * verifier`'s own `OPTIONAL_BUNDLE_FILES` -- their absence degrades the
 * Causal Seam and relation-aware rail rendering, not the whole bundle.
 */
export async function loadEvidenceBundle(
  options: LoadBundleOptions = {},
): Promise<Result<EvidenceBundleView>> {
  const base = options.baseUrl ?? DEFAULT_BUNDLE_BASE;
  const bundle = `${base}/bundle`;

  const [
    manifest,
    receipt,
    vulnerableTrace,
    patchedTrace,
    vulnerableEffects,
    patchedEffects,
    variantLedger,
    artifactIndex,
    alignment,
    divergence,
    verification,
    tamperedVerification,
    intelligence,
  ] = await Promise.all([
    fetchJson(`${bundle}/manifest.json`, ManifestViewSchema),
    fetchJson(`${bundle}/receipt.json`, ReceiptViewSchema),
    fetchJson(`${bundle}/vulnerable/trace.json`, TraceSchema),
    fetchJson(`${bundle}/patched/trace.json`, TraceSchema),
    fetchJson(`${bundle}/vulnerable/effects.json`, EffectsFileSchema),
    fetchJson(`${bundle}/patched/effects.json`, EffectsFileSchema),
    fetchJson(`${bundle}/variants/ledger.json`, VariantLedgerViewSchema),
    fetchJson(`${bundle}/artifact-index.json`, ArtifactIndexViewSchema, { optional: true }),
    fetchJson(`${bundle}/alignment.json`, AlignmentFileSchema, { optional: true }),
    fetchJson(`${bundle}/divergence.json`, DivergenceFileSchema, { optional: true }),
    fetchJson(`${base}/verification.json`, VerificationResultViewSchema, { optional: true }),
    fetchJson(`${base}/verification.tampered.json`, VerificationResultViewSchema, {
      optional: true,
    }),
    fetchJson(`${base}/intelligence.json`, IntelligenceAttestationViewSchema, {
      optional: true,
    }),
  ]);

  if (!manifest.ok) return manifest;
  if (!receipt.ok) return receipt;
  if (!vulnerableTrace.ok) return vulnerableTrace;
  if (!patchedTrace.ok) return patchedTrace;
  if (!vulnerableEffects.ok) return vulnerableEffects;
  if (!patchedEffects.ok) return patchedEffects;
  if (!variantLedger.ok) return variantLedger;
  if (!artifactIndex.ok) return artifactIndex;
  if (!alignment.ok) return alignment;
  if (!divergence.ok) return divergence;
  if (!verification.ok) return verification;
  if (!tamperedVerification.ok) return tamperedVerification;
  if (!intelligence.ok) return intelligence;

  // Each of these required fetches was called without `optional: true`, so
  // `fetchJson` never resolves its `value` to `undefined` on the `ok: true`
  // branch above -- only the `alignment`/`divergence`/verification-result/
  // intelligence fetches below can be legitimately absent.
  if (
    manifest.value === undefined ||
    receipt.value === undefined ||
    vulnerableTrace.value === undefined ||
    patchedTrace.value === undefined ||
    vulnerableEffects.value === undefined ||
    patchedEffects.value === undefined ||
    variantLedger.value === undefined
  ) {
    return err(
      "INTERNAL",
      "a required bundle file resolved to no value despite a successful fetch",
    );
  }
  const manifestValue = manifest.value;
  const receiptValue = receipt.value;
  const vulnerableTraceValue = vulnerableTrace.value;
  const patchedTraceValue = patchedTrace.value;
  const vulnerableEffectsValue = vulnerableEffects.value;
  const patchedEffectsValue = patchedEffects.value;
  const variantLedgerValue = variantLedger.value;

  const emptyAlignment: AlignmentFile = {
    schemaVersion: "1.0",
    caseId: manifestValue.caseId,
    records: [],
  };
  const alignmentValue = alignment.value ?? emptyAlignment;

  const vulnerableRail = buildReplayRail(vulnerableTraceValue, alignmentValue, "vulnerable");
  const patchedRail = buildReplayRail(patchedTraceValue, alignmentValue, "patched");

  const divergenceValue: DivergenceFile | undefined = divergence.value;
  const causalSeam = divergenceValue
    ? buildCausalSeam({
        divergence: divergenceValue,
        vulnerableRail,
        patchedRail,
        vulnerableEffects: vulnerableEffectsValue,
        patchedEffects: patchedEffectsValue,
      })
    : undefined;

  const overview = buildCaseOverview({
    manifest: manifestValue,
    manifestHash: receiptValue.manifestHash,
    generatedAt: receiptValue.generatedAt,
    gates: receiptValue.gates,
    scope: receiptValue.scope,
    variantDenominator: receiptValue.variantDenominator,
  });

  return ok({
    overview,
    vulnerableRail,
    patchedRail,
    vulnerableEffects: vulnerableEffectsValue.effects,
    patchedEffects: patchedEffectsValue.effects,
    causalSeam,
    variantLedger: variantLedgerValue,
    receipt: receiptValue,
    artifactIndex: artifactIndex.value,
    verification: verification.value,
    tamperedVerification: tamperedVerification.value,
    intelligence: intelligence.value,
  });
}
